import { Dispatch, SetStateAction, useEffect } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import { TooltipText } from '../../../components/inputComponents/TooltipText';
import { CheckboxComponent } from "../../../components/inputComponents/CheckboxComponent";

import MultiSelectAutocomplete from '../../../common/MultiSelectAutocomplete/MultiSelectAutocomplete';

const isFormUnchanged = (formikValues: any, initialFormikValues: any) => {
    return JSON.stringify(formikValues) === JSON.stringify(initialFormikValues);
};

interface notificationProps {
    handleTabChange: Dispatch<SetStateAction<boolean>>
    disabledCheckbox : boolean
}

interface values {
    active: boolean,
    notification_emails: string[]
}

const MAX_EMAIL_RECIPIENTS = 5

const NotificationInner = ({handleTabChange, disabledCheckbox}: notificationProps) => {
    const formik = useFormikContext<values>();

    useEffect(() => {
        const hasChanges = !isFormUnchanged(formik.values, formik.initialValues);

        handleTabChange(hasChanges);
    }, [formik.values, formik.initialValues, handleTabChange]);


    const validateEmail = (value: string, selected: string[] ) => {
            const trimmed = value.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
            if (!trimmed) {
                return { error: true, helperText: 'Cannot add an empty email.' };
            }
    
            if (!emailRegex.test(trimmed)){
                return { error: true, helperText: 'Please enter a valid email address.' };
            }
    
            if(selected.includes(trimmed)){
                return { error: true, helperText: 'Email already added.'};
            }
    
            if(selected.length >= MAX_EMAIL_RECIPIENTS) {
                return { error: true, helperText: `Maximum of ${MAX_EMAIL_RECIPIENTS} emails allowed.`};
            }
            
            return { error: false, helperText: '' };
    
        }

    const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        const { checked } = event.target;

        const newActive = !formik.values.active;
        formik.setFieldValue('active', newActive);

        if(!checked){
            formik.setFieldValue('notification_emails', []);
        }
    
    }
    
    return (
        <>
        <CheckboxComponent
            customBoxClass='notification-checkbox'
            fieldName={'active'}
            testingId='voucher-checkbox-notification'
            label={
                <TooltipText
                    text={'Voucher notifications'}
                    textVariant='body1'
                    tooltip='Allows you to send notifications to the emails provided in the Email(s) field.  Three e-mails will be sent when the vouchers reach 50%, 80%, and 100% sold out.'
                    styleName={`validity-checkbox-tooltip ${disabledCheckbox
                                        ? 'disabled'
                                        : ''
                                        }`}
                />
            }
            customAction={handleActiveChange}
            disabledCondition={disabledCheckbox}
         />

         {formik.values.active && (
            <MultiSelectAutocomplete
                ids={formik.values.notification_emails}
                label="Email(s)"
                placeholder=''
                addNewItemLabel='ADD NEW EMAIL'
                dialogTitle='Add A New Email'
                dataTestId='email-notification'
                maxSelection={MAX_EMAIL_RECIPIENTS}
                customValidateModalValue={(value) => validateEmail(value, formik.values.notification_emails)}
                onChange={(newEmails: string[])=>{
                    formik.setFieldValue('notification_emails', newEmails)
                }}
            />
         )}

        <Grid container spacing={2} alignItems="center">
            <Grid item xs={8} md={8}>
                <Typography variant="h3" sx={{ marginTop: 'calc(100vh - 700px)' }} className="links-title">
                    Helpful Links
                </Typography>
            </Grid>
             
            <Grid item xs={4} md={4}>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2,}} className="notificationChangesButtons">
                    {!isFormUnchanged(formik.values, formik.initialValues) && (
                        <Button variant="outlined" onClick={() => formik.resetForm({ values: formik.initialValues })}>
                            Discard
                        </Button>
                    )}
                    <Button variant="contained" color="primary" type="submit" data-testid="voucher-notif-save-button">
                        Save
                    </Button>
                </Box>
            </Grid>
             
        </Grid>

        </>
    )
}

export { NotificationInner }
